import React, {
  useEffect,
  useState
} from 'react';

import {
  Link,
  useParams
} from 'react-router-dom';

import {
  get,
  post
} from '../utils/api';

export default function ResetPassword() {
  const { token } = useParams();

  const [checking, setChecking] =
    useState(true);

  const [valid, setValid] =
    useState(false);

  const [accountName, setAccountName] =
    useState('');

  const [newPassword, setNewPassword] =
    useState('');

  const [
    confirmPassword,
    setConfirmPassword
  ] = useState('');

  const [showPassword, setShowPassword] =
    useState(false);

  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState('');

  const [completed, setCompleted] =
    useState(false);

  useEffect(() => {
    let active = true;

    get(
      '/auth/reset-password/' +
        encodeURIComponent(token || '')
    )
      .then(result => {
        if (!active) return;

        setValid(Boolean(result.valid));
        setAccountName(result.name || '');
      })
      .catch(requestError => {
        if (!active) return;

        setValid(false);
        setError(
          requestError.message ||
            'This password-reset link is invalid or has expired.'
        );
      })
      .finally(() => {
        if (active) {
          setChecking(false);
        }
      });

    return () => {
      active = false;
    };
  }, [token]);

  const submit = async event => {
    event.preventDefault();
    setError('');

    if (newPassword.length < 10) {
      setError(
        'Password must be at least 10 characters.'
      );
      return;
    }

    if (newPassword.length > 128) {
      setError(
        'Password cannot exceed 128 characters.'
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSaving(true);

    try {
      await post(
        '/auth/reset-password/' +
          encodeURIComponent(token || ''),
        {
          new_password: newPassword,
          confirm_password: confirmPassword
        }
      );

      setCompleted(true);
      setNewPassword('');
      setConfirmPassword('');
    } catch (requestError) {
      setError(
        requestError.message ||
          'The password could not be changed.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">
          <h1>BloansBooks</h1>
          <p>Bloans Creations Ltd</p>
        </div>

        {checking && (
          <div
            style={{
              textAlign: 'center',
              padding: '20px 0'
            }}
          >
            <div className="spinner"></div>

            <p
              className="text-muted text-sm"
              style={{ marginTop: 14 }}
            >
              Checking your secure link...
            </p>
          </div>
        )}

        {!checking && completed && (
          <>
            <div
              className="alert"
              style={{
                background: '#E8F5EB',
                border: '1px solid #B8D8BE',
                color: '#2E6B3A',
                marginBottom: 20
              }}
            >
              Your password was changed
              successfully.
            </div>

            <p
              className="text-muted text-sm"
              style={{
                lineHeight: 1.6,
                marginBottom: 20
              }}
            >
              All previous BloansBooks sessions have
              been invalidated. Sign in again using
              your new password.
            </p>

            <Link
              to="/login"
              className="btn btn-primary"
              style={{
                width: '100%',
                justifyContent: 'center'
              }}
            >
              Sign In
            </Link>
          </>
        )}

        {!checking && !completed && !valid && (
          <>
            <div
              className="alert alert-error"
              style={{ marginBottom: 20 }}
            >
              {error ||
                'This password-reset link is invalid or has expired.'}
            </div>

            <p
              className="text-muted text-sm"
              style={{
                lineHeight: 1.6,
                marginBottom: 20
              }}
            >
              Request a new link from the sign-in
              page. Only the most recently issued
              link will work.
            </p>

            <Link
              to="/forgot-password"
              className="btn btn-primary"
              style={{
                width: '100%',
                justifyContent: 'center'
              }}
            >
              Request a New Link
            </Link>
          </>
        )}

        {!checking && !completed && valid && (
          <>
            <h2
              style={{
                fontSize: 20,
                color: 'var(--ink)',
                marginBottom: 8
              }}
            >
              Create a new password
            </h2>

            <p
              className="text-muted text-sm"
              style={{
                lineHeight: 1.6,
                marginBottom: 20
              }}
            >
              {accountName
                ? `Account: ${accountName}. `
                : ''}
              Use at least 10 characters and do not
              reuse your current password.
            </p>

            {error && (
              <div
                className="alert alert-error"
                style={{ marginBottom: 20 }}
              >
                {error}
              </div>
            )}

            <form onSubmit={submit}>
              <div className="form-group">
                <label className="form-label">
                  New password
                </label>

                <div style={{ position: 'relative' }}>
                  <input
                    type={
                      showPassword
                        ? 'text'
                        : 'password'
                    }
                    className="form-control"
                    value={newPassword}
                    onChange={event =>
                      setNewPassword(
                        event.target.value
                      )
                    }
                    minLength={10}
                    maxLength={128}
                    autoComplete="new-password"
                    required
                    autoFocus
                    disabled={saving}
                    style={{ paddingRight: 52 }}
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setShowPassword(
                        previous => !previous
                      )
                    }
                    disabled={saving}
                    style={{
                      position: 'absolute',
                      right: 12,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--ink-light)',
                      fontSize: 12,
                      fontWeight: 500
                    }}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>

                <div className="form-hint">
                  Minimum 10 characters
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  Confirm new password
                </label>

                <input
                  type={
                    showPassword
                      ? 'text'
                      : 'password'
                  }
                  className="form-control"
                  value={confirmPassword}
                  onChange={event =>
                    setConfirmPassword(
                      event.target.value
                    )
                  }
                  minLength={10}
                  maxLength={128}
                  autoComplete="new-password"
                  required
                  disabled={saving}
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                style={{
                  width: '100%',
                  justifyContent: 'center',
                  marginTop: 8
                }}
                disabled={saving}
              >
                {saving
                  ? 'Changing Password...'
                  : 'Change Password'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
